import { useMutation, useQueryClient } from '@tanstack/react-query'

import { api } from '@/lib/api'
import { queryKeys } from '@/lib/query'
import type { Client } from '@/types/domain'

/** Hands a client to another salesperson — its sales contact (`ownerId`).
 *
 * `PUT /directories/clients/:id/owner`. The server allows it only to a holder
 * of `directory.client.manage` (403 otherwise) and only onto a colleague who can
 * hold clients (422 `ownerId: ['not-eligible']` otherwise); the sheet's picker
 * narrows nothing of its own, because the server's rule is the one that holds.
 *
 * Only the client moves. The previous salesperson's quotes and cases stay
 * theirs, so they finish the deals they started.
 *
 * Invalidates the whole `directories` prefix rather than the two reads that
 * show the name. The overview is keyed per year and search and the statement
 * per year, and a mutation fired from the client's page can name neither — and
 * the owner also decides row SCOPE, so the plain directory list and every
 * picker built on it can change membership too. Prefix invalidation is the
 * same shape every other client write in `useDirectory` uses. */
export function useSetClientOwner(clientId: string) {
  const queryClient = useQueryClient()
  return useMutation<Client, Error, string>({
    mutationFn: (ownerId) =>
      api.put<Client>(`/directories/clients/${clientId}/owner`, { ownerId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.directories.all })
    },
  })
}
